import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	collection,
	query,
	orderBy,
	onSnapshot,
	type Timestamp as FirestoreTimestamp,
} from 'firebase/firestore'
import { useState, useEffect } from 'react'
import {
	type LoaderFunctionArgs,
	type MetaFunction,
	useLoaderData,
	data,
} from 'react-router'
import { LivingGlobe } from '#app/components/living-globe'
import { MapHud } from '#app/components/map-hud'
import { getCachedProfiles } from '#app/lib/cache.server'
import { getUser } from '#app/lib/firebase/auth'
import { getFirebaseFirestore } from '#app/lib/firebase/firebase.client'
import { getFirestoreInstance } from '#app/lib/firebase/firebase.server'
import { getSeoMeta } from '#app/utils/seo'
import { makeTimings } from '#app/utils/timing.server'
import { type ProfileData } from '#types/database.types'

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('home loader')

	try {
		// Get user authentication state
		const { user, headers } = await getUser(request)

		// Fetch profiles with caching (2-minute TTL + 1-hour stale-while-revalidate)
		const profiles = await getCachedProfiles(async () => {
			// Get Firestore instance (admin SDK - bypasses security rules)
			const db = getFirestoreInstance()

			// Query profiles collection ordered by created_at descending
			const profilesSnapshot = await db
				.collection('profiles')
				.orderBy('created_at', 'desc')
				.get()

			// Convert Firestore documents to Profile objects
			const profiles: ProfileData[] = profilesSnapshot.docs.map((doc) => {
				const docData = doc.data()
				return {
					id: doc.id,
					user_id: docData.user_id,
					email: docData.email || null,
					twitter_user_id: docData.twitter_user_id || '',
					x_handle: docData.x_handle,
					display_name: docData.display_name || docData.x_handle,
					description: docData.description || null,
					location: docData.location || null,
					avatar_url: docData.avatar_url || null,
					profile_image_url:
						docData.profile_image_url || docData.avatar_url || '',
					profile_banner_url: docData.profile_banner_url || null,
					city_name: docData.city_name,
					lat: docData.lat,
					lng: docData.lng,
					followers_count: docData.followers_count || 0,
					friends_count: docData.friends_count || 0,
					statuses_count: docData.statuses_count || 0,
					favourites_count: docData.favourites_count || 0,
					verified: docData.verified || false,
					protected: docData.protected || false,
					twitter_created_at: docData.twitter_created_at || '',
					lang: docData.lang || null,
					created_at:
						docData.created_at?.toDate?.()?.toISOString() ||
						new Date().toISOString(),
				}
			})

			return profiles
		}, timings)

		return data(
			{ profiles, user },
			{
				headers: {
					...headers,
					'Server-Timing': timings.toString(),
					'Cache-Control': 'private, max-age=60', // Browser caches for 1 minute
				},
			},
		)
	} catch (error) {
		console.error('Error loading profiles:', error)
		return data({ profiles: [] as ProfileData[], user: null })
	}
}

export const meta: MetaFunction = () => {
	return getSeoMeta({
		title: 'Indie World — The Indie Hacker Map',
		description:
			'A vibrant map connecting indie hackers, founders, and makers across the globe. Discover and join thousands of creators building from every corner of the world.',
		keywords:
			'indie hackers, makers, founders, indie hacker map, startup community, global makers, entrepreneur network, digital nomads',
		url: 'https://indieworld.dev/',
	})
}

export const handle: SEOHandle = {
	getSitemapEntries: () => [
		{
			route: '/',
			priority: 1,
			changeFrequency: 'daily',
		},
	],
}

export default function Home() {
	const loaderData = useLoaderData<typeof loader>()

	const [profiles, setProfiles] = useState<ProfileData[]>(
		loaderData?.profiles ?? [],
	)
	const [, setSelectedProfile] = useState<ProfileData | null>(null)

	const user = loaderData?.user

	// Sync profiles state with loader data when it updates
	useEffect(() => {
		if (loaderData?.profiles) {
			setProfiles(loaderData.profiles)
		}
	}, [loaderData])

	// Real-time Firestore listener for profiles (replaces polling)
	useEffect(() => {
		if (typeof window === 'undefined') return

		try {
			const db = getFirebaseFirestore()
			const q = query(collection(db, 'profiles'), orderBy('created_at', 'desc'))

			// Subscribe to real-time updates
			const unsubscribe = onSnapshot(
				q,
				(snapshot) => {
					const updatedProfiles: ProfileData[] = snapshot.docs.map((doc) => {
						const data = doc.data()
						return {
							id: doc.id,
							user_id: data.user_id,
							email: data.email || null,
							twitter_user_id: data.twitter_user_id || '',
							x_handle: data.x_handle,
							display_name: data.display_name || data.x_handle,
							description: data.description || null,
							location: data.location || null,
							avatar_url: data.avatar_url || null,
							profile_image_url:
								data.profile_image_url || data.avatar_url || '',
							profile_banner_url: data.profile_banner_url || null,
							city_name: data.city_name,
							lat: data.lat,
							lng: data.lng,
							followers_count: data.followers_count || 0,
							friends_count: data.friends_count || 0,
							statuses_count: data.statuses_count || 0,
							favourites_count: data.favourites_count || 0,
							verified: data.verified || false,
							protected: data.protected || false,
							twitter_created_at: data.twitter_created_at || '',
							lang: data.lang || null,
							created_at:
								(data.created_at as FirestoreTimestamp)
									?.toDate?.()
									?.toISOString() || new Date().toISOString(),
						}
					})
					setProfiles(updatedProfiles)
				},
				(error) => {
					console.error('Error listening to profiles:', error)
					// Fall back to initial data on error
				},
			)

			return () => unsubscribe()
		} catch (error) {
			console.error('Error setting up Firestore listener:', error)
		}
	}, [])

	const handleProfileClick = (profile: ProfileData) => {
		setSelectedProfile(profile)
		// Open X profile in new tab
		window.open(`https://x.com/${profile.x_handle}`, '_blank')
	}

	return (
		<div className="bg-background min-h-screen">
			{/* Globe */}
			<div className="h-screen w-full">
				{/* HUD Panel - Top Left (only visible when not authenticated) */}
				{!user && <MapHud />}

				<LivingGlobe
					profiles={profiles}
					onProfileClick={handleProfileClick}
					autoZoomToNewest={true}
				/>
			</div>

			{/* Attribution */}
			<div className="fixed bottom-8 left-2 z-40">
				<p className="text-center text-xs text-black">
					Proudly created by{' '}
					<a
						href="https://x.com/gianlucazinn"
						target="_blank"
						rel="noopener noreferrer"
						className="underline decoration-dotted underline-offset-2 transition-colors hover:text-gray-700"
					>
						Gianluca Zin
					</a>
				</p>
			</div>
		</div>
	)
}
